import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  Calendar, 
  Plus, 
  Edit2, 
  Trash2, 
  Euro,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Search,
  Eye,
  Send,
  User,
  Calculator,
  Sparkles,
  ChevronRight,
  Loader2,
  Download,
  Mail,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

const PaymentSchedulesPage = () => {
  const { theme } = useTheme();
  const [schedules, setSchedules] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    title: '',
    total_amount: '',
    num_installments: 3,
    first_date: '',
    frequency: 'monthly',
    include_deposit: true,
    deposit_percent: 30,
    installments: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-generate installments when form values change
  useEffect(() => {
    if (formData.total_amount && formData.num_installments && formData.first_date) {
      generateInstallments();
    }
  }, [formData.total_amount, formData.num_installments, formData.first_date, formData.frequency, formData.include_deposit, formData.deposit_percent]);

  const fetchData = async () => {
    try {
      const [schedulesRes, clientsRes, projectsRes] = await Promise.all([
        api.get('/settings/payment-schedules').catch(() => ({ data: [] })),
        api.get('/users/', { params: { role: 'client' } }).catch(() => ({ data: [] })),
        api.get('/projects/').catch(() => ({ data: [] }))
      ]);
      
      let apiSchedules = schedulesRes.data || [];
      if (Array.isArray(apiSchedules)) {
        apiSchedules = apiSchedules.map(s => ({
          ...s,
          title: s.project_name || s.title,
          installments: s.schedule || s.installments || []
        }));
      }
      
      setSchedules(apiSchedules.length > 0 ? apiSchedules : getDemoSchedules());
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSchedules(getDemoSchedules());
    } finally {
      setLoading(false);
    }
  };

  const getDemoSchedules = () => [
    {
      id: '1',
      client_name: 'TEST SAS',
      client_id: 'c1',
      title: 'Refonte site web',
      total_amount: 8000,
      created_at: '2026-01-01',
      installments: [
        { id: 'i1', amount: 2400, due_date: '2026-01-15', description: 'Acompte 30%', status: 'paid', paid_at: '2026-01-10' },
        { id: 'i2', amount: 2800, due_date: '2026-02-15', description: 'Échéance 2', status: 'pending' },
        { id: 'i3', amount: 2800, due_date: '2026-03-15', description: 'Solde', status: 'pending' }
      ]
    }
  ];

  const generateInstallments = () => {
    const total = parseFloat(formData.total_amount) || 0;
    const numInstallments = parseInt(formData.num_installments) || 1;
    const firstDate = new Date(formData.first_date);
    
    if (!total || !numInstallments || isNaN(firstDate.getTime())) return;

    const installments = [];
    let remainingAmount = total;
    
    // Handle deposit if enabled
    if (formData.include_deposit && formData.deposit_percent > 0) {
      const depositAmount = Math.round((total * formData.deposit_percent / 100) * 100) / 100;
      installments.push({
        amount: depositAmount,
        due_date: formData.first_date,
        description: `Acompte ${formData.deposit_percent}%`,
        status: 'pending'
      });
      remainingAmount -= depositAmount;
    }

    // Calculate remaining installments
    const remainingInstallments = formData.include_deposit ? numInstallments - 1 : numInstallments;
    const installmentAmount = Math.round((remainingAmount / remainingInstallments) * 100) / 100;
    
    // Adjust for rounding errors
    let totalDistributed = formData.include_deposit ? installments[0].amount : 0;

    for (let i = 0; i < remainingInstallments; i++) {
      const date = new Date(firstDate);
      
      // Add months based on frequency
      if (formData.frequency === 'monthly') {
        date.setMonth(date.getMonth() + (formData.include_deposit ? i + 1 : i));
      } else if (formData.frequency === 'biweekly') {
        date.setDate(date.getDate() + (14 * (formData.include_deposit ? i + 1 : i)));
      } else if (formData.frequency === 'weekly') {
        date.setDate(date.getDate() + (7 * (formData.include_deposit ? i + 1 : i)));
      } else if (formData.frequency === 'quarterly') {
        date.setMonth(date.getMonth() + (3 * (formData.include_deposit ? i + 1 : i)));
      }

      // Last installment gets the remainder to avoid rounding issues
      const isLast = i === remainingInstallments - 1;
      const amount = isLast ? Math.round((total - totalDistributed) * 100) / 100 : installmentAmount;
      totalDistributed += amount;

      installments.push({
        amount,
        due_date: date.toISOString().split('T')[0],
        description: isLast ? 'Solde' : `Échéance ${formData.include_deposit ? i + 2 : i + 1}`,
        status: 'pending'
      });
    }

    setFormData(prev => ({ ...prev, installments }));
  };

  const handleSubmit = async () => {
    if (!formData.client_id || !formData.project_id || !formData.title || !formData.total_amount || formData.installments.length === 0) {
      toast.error('Veuillez sélectionner un client, un projet et remplir tous les champs');
      return;
    }

    setSaving(true);
    try {
      const selectedClient = clients.find(c => c.id === formData.client_id);
      const selectedProject = projects.find(p => p.id === formData.project_id);
      
      const payload = {
        client_id: formData.client_id,
        client_name: selectedClient?.company_name || `${selectedClient?.first_name} ${selectedClient?.last_name}`,
        project_id: formData.project_id,
        project_name: selectedProject?.name,
        title: formData.title,
        total_amount: parseFloat(formData.total_amount),
        schedule: formData.installments.map(i => ({
          ...i,
          amount: parseFloat(i.amount)
        }))
      };

      if (editingScheduleId) {
        // Update existing schedule
        await api.put(`/settings/payment-schedules/${editingScheduleId}`, payload);
        toast.success('Échéancier modifié !');
      } else {
        // Create new schedule
        await api.post('/settings/payment-schedules', payload);
        toast.success('Échéancier créé !');
      }
      
      // Also update project payment mode to 'schedule'
      if (formData.project_id) {
        await api.put(`/projects/${formData.project_id}`, {
          payment_mode: 'schedule',
          budget_amount: parseFloat(formData.total_amount)
        }).catch(() => {}); // Silent fail if project update fails
      }
      
      fetchData();
      setShowModal(false);
      resetForm();
      setEditingScheduleId(null);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // State for editing
  const [editingScheduleId, setEditingScheduleId] = useState(null);

  // Edit schedule
  const handleEditSchedule = (schedule, e) => {
    e?.stopPropagation();
    setEditingScheduleId(schedule.id);
    setFormData({
      client_id: schedule.client_id || '',
      project_id: schedule.project_id || '',
      title: schedule.title || '',
      total_amount: schedule.total_amount?.toString() || '',
      num_installments: schedule.installments?.length || 3,
      first_date: schedule.installments?.[0]?.due_date || '',
      frequency: 'monthly',
      include_deposit: false,
      deposit_percent: 30,
      installments: schedule.installments || []
    });
    setShowModal(true);
  };

  // Delete schedule
  const handleDeleteSchedule = async (scheduleId, e) => {
    e?.stopPropagation();
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet échéancier ?')) return;
    
    try {
      await api.delete(`/settings/payment-schedules/${scheduleId}`);
      toast.success('Échéancier supprimé');
      fetchData();
      if (selectedSchedule?.id === scheduleId) {
        setShowDetailModal(false);
        setSelectedSchedule(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Duplicate schedule
  const handleDuplicateSchedule = (schedule, e) => {
    e?.stopPropagation();
    setEditingScheduleId(null); // Important: we're creating a new one
    
    // Calculate new dates (shift all dates by the difference from today)
    const today = new Date();
    const originalFirstDate = new Date(schedule.installments?.[0]?.due_date || today);
    const daysDiff = Math.floor((today - originalFirstDate) / (1000 * 60 * 60 * 24));
    
    const newInstallments = schedule.installments?.map(inst => {
      const newDate = new Date(inst.due_date);
      newDate.setDate(newDate.getDate() + daysDiff + 30); // Start 30 days from now
      return {
        ...inst,
        status: 'pending', // Reset status
        paid_at: null,
        due_date: newDate.toISOString().split('T')[0]
      };
    }) || [];

    setFormData({
      client_id: schedule.client_id || '',
      project_id: '', // Reset project - user should choose a new one
      title: `${schedule.title} (copie)`,
      total_amount: schedule.total_amount?.toString() || '',
      num_installments: schedule.installments?.length || 3,
      first_date: newInstallments[0]?.due_date || today.toISOString().split('T')[0],
      frequency: 'monthly',
      include_deposit: false,
      deposit_percent: 30,
      installments: newInstallments
    });
    setShowModal(true);
    toast.info('Échéancier dupliqué - Modifiez les détails et sauvegardez');
  };

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    schedule_id: '',
    item_number: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'virement'
  });

  const handleOpenPaymentModal = (scheduleId, itemNumber) => {
    setPaymentFormData({
      schedule_id: scheduleId,
      item_number: itemNumber,
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'virement'
    });
    setShowPaymentModal(true);
  };

  const handleMarkPaid = async () => {
    try {
      await api.put(`/settings/payment-schedules/${paymentFormData.schedule_id}/items/${paymentFormData.item_number}/mark-paid`, {
        payment_date: paymentFormData.payment_date,
        payment_method: paymentFormData.payment_method
      });
      toast.success('Paiement enregistré');
      fetchData();
      setShowPaymentModal(false);
      if (selectedSchedule?.id === paymentFormData.schedule_id) {
        const res = await api.get(`/settings/payment-schedules/${paymentFormData.schedule_id}`);
        setSelectedSchedule(res.data);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const [sendingRequest, setSendingRequest] = useState(null);
  
  const sendPaymentRequest = async (scheduleId, projectId, installmentNumber) => {
    setSendingRequest(`${scheduleId}-${installmentNumber}`);
    try {
      await api.post(`/projects/${projectId}/payment-schedule/send-request`, {
        installment_number: installmentNumber
      });
      toast.success('Demande de paiement envoyée au client');
      fetchData();
    } catch (error) {
      console.error('Error sending payment request:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingRequest(null);
    }
  };

  const downloadPdf = async (scheduleId) => {
    try {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/settings/payment-schedules/${scheduleId}/pdf`, '_blank');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      project_id: '',
      title: '',
      total_amount: '',
      num_installments: 3,
      first_date: new Date().toISOString().split('T')[0],
      frequency: 'monthly',
      include_deposit: true,
      deposit_percent: 30,
      installments: []
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Payé</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />En retard</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  // Stats
  const stats = {
    total: schedules.reduce((sum, s) => sum + (s.total_amount || 0), 0),
    paid: schedules.reduce((sum, s) => sum + (s.installments?.filter(i => i.status === 'paid').reduce((a, i) => a + (i.amount || 0), 0) || 0), 0),
    pending: schedules.reduce((sum, s) => sum + (s.installments?.filter(i => i.status === 'pending').reduce((a, i) => a + (i.amount || 0), 0) || 0), 0),
    overdue: schedules.reduce((sum, s) => sum + (s.installments?.filter(i => i.status === 'overdue').reduce((a, i) => a + (i.amount || 0), 0) || 0), 0)
  };

  // Filtered schedules
  const filteredSchedules = schedules.filter(s => {
    const matchesSearch = s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         s.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'active') return matchesSearch && s.installments?.some(i => i.status === 'pending');
    if (activeTab === 'overdue') return matchesSearch && s.installments?.some(i => i.status === 'overdue');
    if (activeTab === 'completed') return matchesSearch && s.installments?.every(i => i.status === 'paid');
    return matchesSearch;
  });

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="payment-schedules-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Échéanciers de paiement
            </h1>
            <p className="text-slate-500 mt-1">
              Gérez les paiements échelonnés de vos clients
            </p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            style={{ backgroundColor: theme.colors?.accent }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel échéancier
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Total engagé</div>
              <div className="text-2xl font-bold text-slate-800">{formatCurrency(stats.total)}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
            <CardContent className="p-4">
              <div className="text-sm text-emerald-600">Encaissé</div>
              <div className="text-2xl font-bold text-emerald-700">{formatCurrency(stats.paid)}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="text-sm text-amber-600">En attente</div>
              <div className="text-2xl font-bold text-amber-700">{formatCurrency(stats.pending)}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4">
              <div className="text-sm text-red-600">En retard</div>
              <div className="text-2xl font-bold text-red-700">{formatCurrency(stats.overdue)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">Tous</TabsTrigger>
              <TabsTrigger value="active">Actifs</TabsTrigger>
              <TabsTrigger value="overdue">En retard</TabsTrigger>
              <TabsTrigger value="completed">Terminés</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Schedules List */}
        <div className="space-y-4">
          {filteredSchedules.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-600 mb-2">Aucun échéancier</h3>
                <p className="text-sm text-slate-400 mb-4">Créez votre premier échéancier de paiement</p>
                <Button onClick={() => setShowModal(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un échéancier
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredSchedules.map((schedule) => {
              const paidCount = schedule.installments?.filter(i => i.status === 'paid').length || 0;
              const totalCount = schedule.installments?.length || 0;
              const progress = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
              const paidAmount = schedule.installments?.filter(i => i.status === 'paid').reduce((a, i) => a + (i.amount || 0), 0) || 0;
              const hasOverdue = schedule.installments?.some(i => i.status === 'overdue');

              return (
                <Card 
                  key={schedule.id} 
                  className={cn(
                    "hover:shadow-md transition-shadow cursor-pointer",
                    hasOverdue && "border-red-200 bg-red-50/30"
                  )}
                  onClick={() => {
                    setSelectedSchedule(schedule);
                    setShowDetailModal(true);
                  }}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-slate-800">{schedule.title}</h3>
                          {hasOverdue && (
                            <Badge className="bg-red-100 text-red-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Retard
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {schedule.client_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(schedule.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-4">
                          <div className="text-lg font-bold text-slate-800">
                            {formatCurrency(schedule.total_amount)}
                          </div>
                          <div className="text-sm text-slate-500">
                            {formatCurrency(paidAmount)} encaissé
                          </div>
                        </div>
                        {/* Action buttons */}
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-blue-600"
                            onClick={(e) => handleEditSchedule(schedule, e)}
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-green-600"
                            onClick={(e) => handleDuplicateSchedule(schedule, e)}
                            title="Dupliquer"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-red-600"
                            onClick={(e) => handleDeleteSchedule(schedule.id, e)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>Progression</span>
                        <span>{paidCount}/{totalCount} échéances</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {schedule.installments?.slice(0, 4).map((inst, idx) => (
                          <div 
                            key={idx}
                            className={cn(
                              "text-xs px-2 py-1 rounded",
                              inst.status === 'paid' ? "bg-emerald-100 text-emerald-700" :
                              inst.status === 'overdue' ? "bg-red-100 text-red-700" :
                              "bg-slate-100 text-slate-600"
                            )}
                          >
                            {formatDate(inst.due_date)} - {formatCurrency(inst.amount)}
                          </div>
                        ))}
                        {schedule.installments?.length > 4 && (
                          <span className="text-xs text-slate-400">+{schedule.installments.length - 4}</span>
                        )}
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Détails
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={(open) => {
          setShowModal(open);
          if (!open) {
            setEditingScheduleId(null);
            resetForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                {editingScheduleId ? 'Modifier l&apos;échéancier' : 'Créer un échéancier'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info - Client and Project */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client <span className="text-red-500">*</span></Label>
                  <Select 
                    value={formData.client_id} 
                    onValueChange={(v) => {
                      const client = clients.find(c => c.id === v);
                      setFormData({ 
                        ...formData, 
                        client_id: v,
                        project_id: '' // Reset project when client changes
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name || `${client.first_name} ${client.last_name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Projet lié <span className="text-red-500">*</span></Label>
                  <Select 
                    value={formData.project_id} 
                    onValueChange={(v) => {
                      const project = projects.find(p => p.id === v);
                      if (project) {
                        setFormData({ 
                          ...formData, 
                          project_id: v,
                          title: project.name,
                          total_amount: project.budget_amount || project.budget_hours * 100 || '',
                          client_id: project.client_id || formData.client_id
                        });
                      } else {
                        setFormData({ ...formData, project_id: v });
                      }
                    }}
                    disabled={!formData.client_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.client_id ? "Sélectionner un projet" : "Sélectionnez d'abord un client"} />
                    </SelectTrigger>
                    <SelectContent>
                      {projects
                        .filter(p => !formData.client_id || p.client_id === formData.client_id)
                        .map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name} {project.budget_amount ? `(${project.budget_amount}€)` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Title (auto-filled from project) */}
              <div className="space-y-2">
                <Label>Titre de l'échéancier</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Refonte site web"
                />
              </div>

              {/* Amount and Installments */}
              <Card className="bg-slate-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    Générateur automatique
                  </CardTitle>
                  <CardDescription>
                    Configurez les paramètres pour générer automatiquement les échéances
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Montant total (€)</Label>
                      <Input
                        type="number"
                        value={formData.total_amount}
                        onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                        placeholder="5000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre d'échéances</Label>
                      <Select 
                        value={formData.num_installments.toString()} 
                        onValueChange={(v) => setFormData({ ...formData, num_installments: parseInt(v) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                            <SelectItem key={n} value={n.toString()}>
                              {n} échéances
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Première échéance</Label>
                      <Input
                        type="date"
                        value={formData.first_date}
                        onChange={(e) => setFormData({ ...formData, first_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fréquence</Label>
                      <Select 
                        value={formData.frequency} 
                        onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          <SelectItem value="biweekly">Bi-mensuelle</SelectItem>
                          <SelectItem value="monthly">Mensuelle</SelectItem>
                          <SelectItem value="quarterly">Trimestrielle</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-white border">
                    <div>
                      <Label>Inclure un acompte</Label>
                      <p className="text-xs text-slate-500">Première échéance en pourcentage</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {formData.include_deposit && (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={formData.deposit_percent}
                            onChange={(e) => setFormData({ ...formData, deposit_percent: parseInt(e.target.value) || 0 })}
                            className="w-16 h-8 text-center"
                            min="0"
                            max="100"
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                      )}
                      <Switch
                        checked={formData.include_deposit}
                        onCheckedChange={(checked) => setFormData({ ...formData, include_deposit: checked })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preview */}
              {formData.installments.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Aperçu des échéances</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Échéance</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Montant</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.installments.map((inst, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{inst.description}</TableCell>
                            <TableCell>{formatDate(inst.due_date)}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(inst.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50">
                          <TableCell colSpan={2} className="font-bold">Total</TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(formData.installments.reduce((sum, i) => sum + i.amount, 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving || formData.installments.length === 0}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer l'échéancier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedSchedule?.title}</DialogTitle>
            </DialogHeader>

            {selectedSchedule && (
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                  <div>
                    <div className="text-sm text-slate-500">Client</div>
                    <div className="font-medium">{selectedSchedule.client_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Montant total</div>
                    <div className="text-xl font-bold">{formatCurrency(selectedSchedule.total_amount)}</div>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N°</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Échéance</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedSchedule.installments?.map((inst, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell className="font-medium">{inst.description}</TableCell>
                        <TableCell>{formatDate(inst.due_date)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(inst.amount)} HT
                        </TableCell>
                        <TableCell>{getStatusBadge(inst.status)}</TableCell>
                        <TableCell>
                          {inst.status !== 'paid' ? (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendPaymentRequest(selectedSchedule.id, selectedSchedule.project_id, idx + 1);
                                }}
                                disabled={sendingRequest === `${selectedSchedule.id}-${idx + 1}`}
                                title="Envoyer une demande de paiement au client"
                              >
                                {sendingRequest === `${selectedSchedule.id}-${idx + 1}` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPaymentModal(selectedSchedule.id, idx + 1);
                                }}
                                title="Marquer comme payé"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-500">
                              {inst.payment_date && (
                                <span>{new Date(inst.payment_date).toLocaleDateString('fr-FR')}</span>
                              )}
                              {inst.payment_method && (
                                <span className="ml-2">({inst.payment_method})</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Download PDF Button */}
                <div className="p-4 border-t">
                  <Button variant="outline" onClick={() => downloadPdf(selectedSchedule.id)}>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Modal */}
        <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Enregistrer le paiement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Date du paiement</Label>
                <Input
                  type="date"
                  value={paymentFormData.payment_date}
                  onChange={(e) => setPaymentFormData({...paymentFormData, payment_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Mode de paiement</Label>
                <Select 
                  value={paymentFormData.payment_method} 
                  onValueChange={(v) => setPaymentFormData({...paymentFormData, payment_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                    <SelectItem value="carte">Carte bancaire</SelectItem>
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="wallet">Wallet client</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Annuler</Button>
              <Button onClick={handleMarkPaid} style={{ backgroundColor: theme.colors?.accent }}>
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default PaymentSchedulesPage;
